import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { WalletAPI } from '../services/api';

const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [walletData, setWalletData] = useState({
    balance: 0,
    locked_balance: 0,
    available_balance: 0,
    bitcoin_address: null,
    transactions: [],
    isLoading: false,
    error: null
  });

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch wallet balance
  const fetchBalance = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (walletData.isLoading) {
      console.log('⚠️ fetchBalance: Already loading, skipping...');
      return;
    }
    
    // Add timestamp to prevent too frequent calls
    const now = Date.now();
    const lastCall = walletData.lastFetchTime || 0;
    const timeSinceLastCall = now - lastCall;
    
    if (timeSinceLastCall < 50000) { // 5 seconds minimum between calls
      console.log(`⏱️ fetchBalance: Too soon (${timeSinceLastCall}ms), skipping...`);
      return;
    }
    
    try {
      console.log('💰 fetchBalance: Starting...');
      setWalletData(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await WalletAPI.getBalance();
      
      console.log('💰 fetchBalance: Response received');
      
      if (response && response.success !== false) {
        const newData = {
          balance: response.balance || 0,
          locked_balance: response.locked_balance || 0,
          available_balance: response.available_balance || 0,
          bitcoin_address: response.bitcoin_address || null,
          isLoading: false,
          error: null,
          lastFetchTime: Date.now()
        };
        setWalletData(newData);
        setLastUpdated(new Date());
        return newData;
      } else {
        throw new Error(response?.error || 'Failed to fetch balance');
      }
    } catch (error) {
      console.error('💥 fetchBalance error:', error);
      setWalletData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch wallet data'
      }));
      throw error;
    }
  }, [walletData.isLoading, walletData.lastFetchTime]); // Add dependencies

  // Fetch transactions
  const fetchTransactions = useCallback(async (limit = 20, page = 1) => {
    try {
      setTransactionsLoading(true);
      const response = await WalletAPI.getTransactions(limit, page);
      
      if (response && response.success !== false) {
        setTransactions(response.transactions || []);
        return response.transactions || [];
      } else {
        throw new Error(response?.error || 'Failed to fetch transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  // Refresh wallet data
  const refreshWallet = useCallback(async () => {
    await Promise.all([
      fetchBalance(),
      fetchTransactions()
    ]);
  }, [fetchBalance, fetchTransactions]);

  // Auto-refresh wallet every 30 seconds if balance > 0
  useEffect(() => {
    let intervalId;
    
    if (walletData.balance > 0 && !walletData.isLoading) {
      intervalId = setInterval(() => {
        fetchBalance();
      }, 30000); // 30 seconds
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [walletData.balance, walletData.isLoading, fetchBalance]);

  // Format currency helper
  const formatCurrency = useCallback((amount) => {
    return parseFloat(amount || 0).toFixed(8).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }, []);

  // Get formatted balances
  const getFormattedBalances = useCallback(() => {
    return {
      balance: formatCurrency(walletData.balance),
      locked: formatCurrency(walletData.locked_balance),
      available: formatCurrency(walletData.available_balance)
    };
  }, [walletData, formatCurrency]);

  const value = {
    walletData,
    transactions,
    transactionsLoading,
    lastUpdated,
    fetchBalance,
    fetchTransactions,
    refreshWallet,
    formatCurrency,
    getFormattedBalances,
    isLoading: walletData.isLoading,
    error: walletData.error
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};